import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, useAbruf, type ApiFehler, type Seite } from '../api'
import { Fehleranzeige, Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, geld } from '../formate'

interface Rechnungszeile {
  id: string
  rechnungsnummer: string
  art: string
  status: string
  rechnungsdatum: string
  faelligAm: string
  zeitraumVon: string
  zeitraumBis: string
  bruttobetrag: string
  zahlbetrag: string
  guthaben: boolean
}

// Leer heisst: alle. Ein fester Vorgabezustand taugt hier nicht - eine Rechnung ist
// nur zwischen dem Festschreiben und dem Erzeugen ihres Belegs festgeschrieben.
// Aufzaehlung: Rechnungsstatus
const zustaende = ['', 'ENTWURF', 'FESTGESCHRIEBEN', 'VERSENDET', 'STORNIERT']
// Aufzaehlung: Rechnungsart
const arten = [
  'TURNUSRECHNUNG',
  'SCHLUSSRECHNUNG',
  'ZWISCHENRECHNUNG',
  'ABSCHLAGSRECHNUNG',
  'STORNORECHNUNG',
]

export default function Rechnungen() {
  const [status, setzeStatus] = useState('')
  const [seite, setzeSeite] = useState(0)
  const treffer = useAbruf<Seite<Rechnungszeile>>(
    `/api/rechnungen?${status ? `status=${status}&` : ''}seite=${seite}&groesse=25`,
  )

  const [vertragsnummer, setzeVertragsnummer] = useState('')
  const [von, setzeVon] = useState('2026-01-01')
  const [bis, setzeBis] = useState('2026-12-31')
  const [art, setzeArt] = useState('TURNUSRECHNUNG')
  const [laeuft, setzeLaeuft] = useState(false)
  const [meldung, setzeMeldung] = useState<string>()
  const [fehler, setzeFehler] = useState<ApiFehler>()

  async function abrechnen() {
    setzeMeldung(undefined)
    setzeFehler(undefined)
    setzeLaeuft(true)
    try {
      const rechnung = await api.sende<Rechnungszeile>('/api/rechnungen/laeufe', {
        vertragsnummer,
        von,
        bis,
        art,
        rechnungsdatum: new Date().toISOString().slice(0, 10),
      })
      setzeMeldung(`Rechnung ${rechnung.rechnungsnummer} wurde festgeschrieben.`)
      treffer.erneut()
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler)
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <>
      <h1>Rechnungen</h1>
      <p className="untertitel">
        Eine festgeschriebene Rechnung ist unveränderlich. Eine Korrektur erfolgt über
        eine Storno- und eine neue Rechnung.
      </p>

      <Karte titel="Vertrag abrechnen">
        {meldung && <div className="hinweis info">{meldung}</div>}
        {fehler && <Fehleranzeige fehler={fehler} />}
        <div className="leiste" style={{ marginBottom: 0 }}>
          <div className="feld">
            <label htmlFor="vertrag">Vertragsnummer</label>
            <input
              id="vertrag"
              style={{ width: 200 }}
              placeholder="V-2026-000001"
              value={vertragsnummer}
              onChange={(ereignis) => setzeVertragsnummer(ereignis.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="von">Von</label>
            <input
              id="von"
              type="date"
              value={von}
              onChange={(ereignis) => setzeVon(ereignis.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="bis">Bis</label>
            <input
              id="bis"
              type="date"
              value={bis}
              onChange={(ereignis) => setzeBis(ereignis.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="art">Art</label>
            <select
              id="art"
              value={art}
              onChange={(ereignis) => setzeArt(ereignis.target.value)}
            >
              {arten.map((wert) => (
                <option key={wert} value={wert}>
                  {wert}
                </option>
              ))}
            </select>
          </div>
          <button className="betont" disabled={!vertragsnummer || laeuft} onClick={abrechnen}>
            {laeuft ? 'Rechnet ab …' : 'Abrechnen'}
          </button>
        </div>
      </Karte>

      <div className="leiste">
        <div className="feld">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={status}
            onChange={(ereignis) => {
              setzeSeite(0)
              setzeStatus(ereignis.target.value)
            }}
          >
            {zustaende.map((wert) => (
              <option key={wert} value={wert}>
                {wert === '' ? 'Alle' : wert}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Karte>
        <Zustand
          laeuft={treffer.laeuft}
          fehler={treffer.fehler}
          leer={(treffer.daten?.inhalt ?? []).length === 0}
          leertext="Keine Rechnung in diesem Zustand."
        >
          <Tabelle
            spalten={[
              { name: 'Rechnungsnummer' },
              { name: 'Art' },
              { name: 'Status' },
              { name: 'Datum' },
              { name: 'Zeitraum' },
              { name: 'Brutto', zahl: true },
              { name: 'Zahlbetrag', zahl: true },
            ]}
          >
            {(treffer.daten?.inhalt ?? []).map((rechnung) => (
              <tr key={rechnung.id}>
                <td>
                  <Link to={`/rechnungen/${rechnung.rechnungsnummer}`}>
                    {rechnung.rechnungsnummer}
                  </Link>
                </td>
                <td>{rechnung.art}</td>
                <td>
                  <Statusmerkmal status={rechnung.status} />
                </td>
                <td>{datum(rechnung.rechnungsdatum)}</td>
                <td>
                  {datum(rechnung.zeitraumVon)} – {datum(rechnung.zeitraumBis)}
                </td>
                <td className="zahl">{geld(rechnung.bruttobetrag)}</td>
                <td className="zahl">
                  {geld(rechnung.zahlbetrag)}{' '}
                  {rechnung.guthaben && <Merkmal art="gut">Guthaben</Merkmal>}
                </td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>
    </>
  )
}

/**
 * Der Zustand einer Rechnung als farbiges Merkmal.
 *
 * <p>Seit die Liste voreingestellt alle Zustaende zeigt, muss die Zeile selbst sagen,
 * in welchem sie steht.
 */
function Statusmerkmal({ status }: { status: string }) {
  const art =
    status === 'VERSENDET'
      ? 'gut'
      : status === 'STORNIERT'
        ? 'fehler'
        : status === 'ENTWURF'
          ? 'warnung'
          : 'betont'
  return <Merkmal art={art}>{status}</Merkmal>
}
